import { forwardRef, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Camera, Group, Object3D, Plane, Raycaster, Vector2, Vector2Tuple, Vector3 } from 'three';

import { Html, useCursor } from '@react-three/drei';
import { GroupProps, MeshProps, ThreeEvent, useThree } from '@react-three/fiber';
import { useEventCallback, useForceUpdate } from '@spazious/react-hooks';
import { useDrag } from '@use-gesture/react';

import { Polygon } from '../Polygon/index.js';
import { DragEvent } from '../RectDrawControls/index.js';

/**
 * TODO -
 * remove measure,
 * drag handler to modify position,
 * add persitence to component
 *
 */
export interface RulerControlHandleProps extends MeshProps {
  onDrag?: (payload: { position: Vector2 }) => void;
  onRemove?: () => void;
  hoverCursor?: string;
  color?: string;
  size?: number;
}

export const RulerControlHandle: React.FC<RulerControlHandleProps> = ({
  onDrag,
  onRemove,
  color = 'white',
  hoverCursor = 'move',
  size = 1,
  ...restProps
}) => {
  const [hover, setHover] = useState(false);
  const { controls, camera, pointer, raycaster } = useThree();
  const bind = useDrag(({ event, down }) => {
    event.stopPropagation();
    const plane = new Plane(new Vector3(0, 1, 0), 0);
    const pos = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, pos);
    onDrag?.({ position: new Vector2(pos.x, pos.z) });

    if (controls) {
      (controls as any).enabled = !down;
    }
  });

  useCursor(hover, hoverCursor);

  const handlePointerOver = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(true);
  });

  const handlePointerOut = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(false);
  });

  return (
    <mesh
      {...(bind() as any)}
      {...restProps}
      onDoubleClick={onRemove}
      onPointerOut={handlePointerOut}
      onPointerOver={handlePointerOver}
    >
      <sphereGeometry args={[size, 10, 10]} />
      <meshBasicMaterial color={color} depthTest={false} />
    </mesh>
  );
};

export const getDistance = (source: Vector3, target: Vector3, minDistance: number) => {
  const direction = new Vector3().subVectors(target, source).normalize();
  const distance = Math.max(minDistance, source.distanceTo(target));
  direction.multiplyScalar(distance).add(source).toArray();

  return distance;
};

const fromPointer = (
  raycaster: Raycaster,
  pointer: Vector2,
  camera: Camera,
  target: Vector3 = new Vector3(),
  objects: Object3D[]
) => {
  raycaster.setFromCamera(pointer, camera);
  const intersects = raycaster.intersectObjects(objects);
  if (intersects?.length > 0) {
    target.copy(intersects[0].point);
  }
  return target;
};

const _va = new Vector3();
const _vb = new Vector3();

export interface RulerControlsProps extends GroupProps {
  component?: React.ComponentType<{
    children: React.ReactNode;
  }>;
  objects?: Object3D[];
  onDraw?: (evt: DragEvent) => void;
  onDrawEnd?: (evt: DragEvent) => void;
  onChange?: (evt: { type: 'remove' | 'add' | 'update'; points: Vector2Tuple[] }) => void;
}

export const RulerControls = forwardRef<Group, RulerControlsProps>(
  ({ objects, component: Component = 'div' }, ref) => {
    const [dragging, setDragging] = useState(false);
    const [handlePositions, setHandlePositions] = useState<Vector3[]>([]);
    const [htmlElement, setHtmlElement] = useState<HTMLDivElement | null>(null);
    const [distance, setDistance] = useState(0);
    const controls = useThree(({ controls }) => controls);
    const events = useThree(({ events }) => events);
    const pointer = useThree(({ pointer }) => pointer);
    const camera = useThree(({ camera }) => camera);
    const raycaster = useThree(({ raycaster }) => raycaster);
    const setEvents = useThree(({ setEvents }) => setEvents);
    const size: number = 0.15;

    const forceUpdate = useForceUpdate();

    const minSize = 0;
    let [polygonId, setPolygonId] = useState<number>(0);

    const polygonList: {
      va: { x: number; z: number };
      vb?: { x: number; z: number };
      id: number;
      distance?: number;
    }[] = useMemo(() => [], []);

    const updatePosition = useCallback(
      (target: Vector3) => {
        return fromPointer(raycaster, pointer, camera, target, objects!);
      },
      [camera, objects, pointer, raycaster]
    );

    const handlerMove = useCallback(() => {
      if (!dragging) {
        return;
      }
      updatePosition(_vb).toArray();

      const actualPolygonIndex = polygonList.findIndex((polygon) => polygon.id === polygonId);
      polygonList[actualPolygonIndex].vb = { x: _vb.x, z: _vb.z };

      const measuredDistance = getDistance(_va, updatePosition(_vb), minSize);
      setDistance(measuredDistance);
      polygonList[actualPolygonIndex].distance = distance;

      forceUpdate();
    }, [distance, dragging, forceUpdate, polygonId, polygonList, updatePosition]);

    const handlerDown = useCallback(() => {
      updatePosition(_va).toArray();

      polygonList.push({ id: polygonId, va: { x: _va.x, z: _va.z }, vb: { x: _va.x, z: _va.z } });
      const newPositionHandler = new Vector3(_va.x, 0, _va.z);
      setHandlePositions((prevPositions) => [...prevPositions, newPositionHandler]);

      setDragging(true);
    }, [polygonId, polygonList, updatePosition]);

    const handlerUp = useCallback(() => {
      getDistance(_va, updatePosition(_vb), minSize);
      const actualPolygonIndex = polygonList.findIndex((polygon) => polygon.id === polygonId);
      polygonList[actualPolygonIndex].vb = { x: _vb.x, z: _vb.z };

      const newPositionHandler = new Vector3(_vb.x, 0, _vb.z);
      setHandlePositions((prevPositions) => [...prevPositions, newPositionHandler]);

      const newPolygonId = polygonId + 1;
      setPolygonId(newPolygonId);

      setDragging(false);
    }, [polygonId, polygonList, updatePosition]);

    const attachEvents = useCallback(() => {
      events.connected?.addEventListener('pointermove', handlerMove);
      events.connected?.addEventListener('pointerdown', handlerDown);
      events.connected?.addEventListener('pointerup', handlerUp);
    }, [events.connected, handlerDown, handlerMove, handlerUp]);

    const detachEvents = useCallback(() => {
      events.connected?.removeEventListener('pointermove', handlerMove);
      events.connected?.removeEventListener('pointerdown', handlerDown);
      events.connected?.removeEventListener('pointerup', handlerUp);
    }, [events.connected, handlerDown, handlerMove, handlerUp]);

    useEffect(() => {
      attachEvents();
      return () => {
        detachEvents();
      };
    }, [attachEvents, detachEvents]);

    useEffect(() => {
      setEvents({ enabled: dragging });
      if (controls) {
        (controls as any).enabled = !dragging;
      }

      return () => {
        if (controls) {
          (controls as any).enabled = true;
        }
        setEvents({ enabled: true });
      };
    }, [controls, dragging, setEvents]);

    useEffect(() => {
      if (htmlElement && htmlElement.parentElement) {
        htmlElement.parentElement.style.pointerEvents = 'none';
      }
    }, [htmlElement]);

    return (
      <group>
        {polygonList.map((info) => {
          return (
            <Fragment key={info.id}>
              <Polygon
                ref={ref}
                color="orange"
                lineWidth={2}
                points={[new Vector2(info.va.x, info.va.z), new Vector2(info.vb?.x, info.vb?.z)]}
              />
              <Html
                ref={setHtmlElement}
                center
                position={new Vector3().lerpVectors(
                  new Vector3(info.va.x, 0, info.va.z),
                  new Vector3(info.vb?.x, 0, info.vb?.z),
                  0.5
                )}
              >
                <Component>{(info.distance?.toFixed(2) || '0.00') + 'm'}</Component>
              </Html>
            </Fragment>
          );
        })}
        {handlePositions.map((position, index) => (
          <RulerControlHandle
            key={index}
            hoverCursor="crosshair"
            position={position}
            size={size}
            // onDrag={({ position }) => {
            //   const newPoints = handlePositions.map((p, i) => (i === index ? position : p));
            //   const newPointsTuple = newPoints
            //     .map((p) => [p.x, p.y] as Vector2Tuple)
            //     .filter((p) => p.length === 2);
            //   onChange?.({ type: 'update', points: newPointsTuple });
            // }}
            // onRemove={() => {
            //   const newPoints = handlePositions.filter((p, i) => i !== index);
            //   const newPointsTuple = newPoints
            //     .map((p) => [p.x, p.y] as Vector2Tuple)
            //     .filter((p) => p.length === 2);
            //   onChange?.({ type: 'remove', points: newPointsTuple });
            // }}
          />
        ))}
      </group>
    );
  }
);
